import React, { useCallback, useState } from 'react';
import type { Editor, NodeViewProps } from '@tiptap/react';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import TabOutlined from '@mui/icons-material/TabOutlined';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTranslate } from 'react-admin';
import { BlockFrame } from './BlockFrame';
import { AttrTextField } from './AttrFields';
import type { Block } from '../types';
import { BlockEditor } from '../BlockEditor';
import { gridTemplate } from '../blockUtils';

/**
 * Nested editor for one container of a layout block (column, accordion panel,
 * tab). A separate, compact BlockEditor instance whose blocks are written back
 * into the parent node attribute. Layout blocks cannot be nested (plan §6.7).
 */
export const NestedBlocksEditor: React.FC<{ blocks: Block[]; onChange: (blocks: Block[]) => void; label: string; onEditorReady?: (editor: Editor | null) => void }> = ({ blocks, onChange, label, onEditorReady }) => {
    const onUpdate = useCallback((next: Block[]) => onChange(next), [onChange]);
    return (
        <Box className="oa-nested-editor" aria-label={label}>
            <BlockEditor blocks={blocks} onUpdate={onUpdate} nested onEditorReady={onEditorReady} />
        </Box>
    );
};

const Remove: React.FC<{ onClick: () => void; disabled?: boolean; label: string }> = ({ onClick, disabled, label }) => (
    <Tooltip title={label}>
        <span><IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}><DeleteOutline fontSize="small" /></IconButton></span>
    </Tooltip>
);

/* ───────────────────────── Columns ───────────────────────── */

interface Column { width?: string; blocks: Block[] }

const PRESETS: { key: string; columns: Column[] }[] = [
    { key: '2', columns: [{ width: '1/2', blocks: [] }, { width: '1/2', blocks: [] }] },
    { key: '3', columns: [{ width: '1/3', blocks: [] }, { width: '1/3', blocks: [] }, { width: '1/3', blocks: [] }] },
    { key: '2-1', columns: [{ width: '2/3', blocks: [] }, { width: '1/3', blocks: [] }] },
    { key: '1-2', columns: [{ width: '1/3', blocks: [] }, { width: '2/3', blocks: [] }] },
];

export const ColumnsBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const columns: Column[] = Array.isArray(node.attrs.columns) ? (node.attrs.columns as Column[]) : [];
    const setColumns = (next: Column[]) => updateAttributes({ columns: next });
    const [choosing, setChoosing] = useState(columns.length === 0);

    if (choosing || columns.length === 0) {
        return (
            <BlockFrame type="columns-block" icon={ViewColumnOutlined} title={translate('blockEditor.types.columns.label')} selected={selected}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>{translate('blockEditor.views.columns.choose')}</Typography>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                    {PRESETS.map((p) => (
                        <Button key={p.key} size="small" variant="outlined" onClick={() => { setColumns(p.columns.map((c, i) => ({ ...c, blocks: columns[i]?.blocks ?? [] }))); setChoosing(false); }}>
                            {translate(`blockEditor.views.columns.preset.${p.key}`)}
                        </Button>
                    ))}
                </Stack>
            </BlockFrame>
        );
    }

    return (
        <BlockFrame
            type="columns-block"
            icon={ViewColumnOutlined}
            title={translate('blockEditor.types.columns.label')}
            meta={translate('blockEditor.views.columns.count', { smart_count: columns.length })}
            selected={selected}
            actions={
                <Stack direction="row" spacing={0.5}>
                    <Button size="small" onClick={() => setChoosing(true)}>{translate('blockEditor.views.columns.changeLayout')}</Button>
                    {columns.length < 4 && <Button size="small" startIcon={<Add />} onClick={() => setColumns([...columns, { width: `1/${columns.length + 1}`, blocks: [] }])}>{translate('blockEditor.views.columns.add')}</Button>}
                </Stack>
            }
        >
            <Box className="oa-block-columns" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: gridTemplate(columns) }, gap: 1.5 }}>
                {columns.map((col, i) => (
                    <Box key={i} className="oa-block-column">
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.columns.column', { n: i + 1 })}</Typography>
                            {columns.length > 2 && <Remove onClick={() => setColumns(columns.filter((_, k) => k !== i))} label={translate('blockEditor.views.columns.remove')} />}
                        </Stack>
                        <NestedBlocksEditor label={translate('blockEditor.views.columns.column', { n: i + 1 })} blocks={col.blocks ?? []} onChange={(blocks) => setColumns(columns.map((c, k) => (k === i ? { ...c, blocks } : c)))} />
                    </Box>
                ))}
            </Box>
        </BlockFrame>
    );
};

/* ───────────────────────── Accordion ───────────────────────── */

interface AccordionItem { title: string; blocks: Block[]; defaultOpen?: boolean }

export const AccordionBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const items: AccordionItem[] = Array.isArray(node.attrs.items) ? (node.attrs.items as AccordionItem[]) : [];
    const allowMultiple = (node.attrs.allowMultiple as boolean) ?? false;
    const [open, setOpen] = useState<number | null>(0);
    const setItems = (next: AccordionItem[]) => updateAttributes({ items: next });
    const update = (i: number, patch: Partial<AccordionItem>) => setItems(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));

    return (
        <BlockFrame
            type="accordion-block"
            icon={UnfoldMore}
            title={translate('blockEditor.types.accordion.label')}
            meta={translate('blockEditor.views.accordion.count', { smart_count: items.length })}
            selected={selected}
            flush
            actions={
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={allowMultiple} onChange={(e) => updateAttributes({ allowMultiple: e.target.checked })} />} label={<Typography variant="caption">{translate('blockEditor.views.accordion.allowMultiple')}</Typography>} />
                    <Button size="small" startIcon={<Add />} onClick={() => { setItems([...items, { title: translate('blockEditor.views.accordion.newItem', { n: items.length + 1 }), blocks: [], defaultOpen: false }]); setOpen(items.length); }}>{translate('blockEditor.views.accordion.add')}</Button>
                </Stack>
            }
        >
            {items.map((item, i) => (
                <Box key={i} className="oa-block-accordion-item">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
                        <IconButton size="small" onClick={() => setOpen(open === i ? null : i)} aria-label={translate(open === i ? 'blockEditor.views.accordion.collapse' : 'blockEditor.views.accordion.expand')}>
                            {open === i ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                        </IconButton>
                        <AttrTextField size="small" variant="standard" fullWidth value={item.title} onValueChange={(v) => update(i, { title: v })} placeholder={translate('blockEditor.views.accordion.titlePlaceholder')} InputProps={{ disableUnderline: true, sx: { fontWeight: 600 } }} />
                        <FormControlLabel sx={{ mr: 0, whiteSpace: 'nowrap' }} control={<Switch size="small" checked={item.defaultOpen ?? false} onChange={(e) => update(i, { defaultOpen: e.target.checked })} />} label={<Typography variant="caption">{translate('blockEditor.views.accordion.defaultOpen')}</Typography>} />
                        <Remove onClick={() => { setItems(items.filter((_, k) => k !== i)); setOpen(null); }} disabled={items.length <= 1} label={translate('blockEditor.views.accordion.remove')} />
                    </Stack>
                    {open === i && (
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                            <NestedBlocksEditor label={item.title} blocks={item.blocks ?? []} onChange={(blocks) => update(i, { blocks })} />
                        </Box>
                    )}
                </Box>
            ))}
        </BlockFrame>
    );
};

/* ───────────────────────── Tabs ───────────────────────── */

interface TabItem { label: string; blocks: Block[] }

export const TabsBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const tabs: TabItem[] = Array.isArray(node.attrs.tabs) ? (node.attrs.tabs as TabItem[]) : [];
    const [active, setActive] = useState(0);
    const current = Math.min(active, Math.max(tabs.length - 1, 0));
    const setTabs = (next: TabItem[]) => updateAttributes({ tabs: next });
    const update = (i: number, patch: Partial<TabItem>) => setTabs(tabs.map((t, k) => (k === i ? { ...t, ...patch } : t)));

    return (
        <BlockFrame
            type="tabs-block"
            icon={TabOutlined}
            title={translate('blockEditor.types.tabs.label')}
            meta={translate('blockEditor.views.tabs.count', { smart_count: tabs.length })}
            selected={selected}
            flush
            actions={<Button size="small" startIcon={<Add />} onClick={() => { setTabs([...tabs, { label: translate('blockEditor.views.tabs.newTab', { n: tabs.length + 1 }), blocks: [] }]); setActive(tabs.length); }}>{translate('blockEditor.views.tabs.add')}</Button>}
        >
            <Tabs value={current} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: '1px solid var(--oa-divider)', minHeight: 40 }}>
                {tabs.map((tab, i) => <Tab key={i} label={tab.label || translate('blockEditor.views.tabs.newTab', { n: i + 1 })} sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }} />)}
            </Tabs>
            {tabs[current] && (
                <Box sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <AttrTextField size="small" label={translate('blockEditor.views.tabs.label')} value={tabs[current].label} onValueChange={(v) => update(current, { label: v })} sx={{ maxWidth: 280 }} />
                        <Remove onClick={() => { setTabs(tabs.filter((_, k) => k !== current)); setActive(Math.max(0, current - 1)); }} disabled={tabs.length <= 1} label={translate('blockEditor.views.tabs.remove')} />
                    </Stack>
                    <NestedBlocksEditor label={tabs[current].label} blocks={tabs[current].blocks ?? []} onChange={(blocks) => update(current, { blocks })} />
                </Box>
            )}
        </BlockFrame>
    );
};
